import { FC } from 'react';
import { useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';

import * as K from 'common/constants';
import { numericStringCompare } from 'common/util/string';
import ELEMENT, { createTestId } from 'common/element';

import Table, { TableRow } from 'components/common/Table/Table';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';

import { RESEND_INVITE, REVOKE_ACCESS, REVOKE_INVITE } from '../queries';
import { USER_STATUS, UserAccount, UserAccountData } from '../types';
import { makeRowData } from '../helpers';
import { TABLE_HEADERS } from '../consts';
import '../UserAccountPage.scss';

/**
 * Page that allows user with admin rights to manage access/restrictions per user
 *
 * @param {function} userAccountData        Data that has been recieved from query that contain user accounts data
 * @param {boolean} userAccountLoading      Boolean that shows whether user data is loading or not
 * @returns {Component}
 */

const tableOptions = {
  isStriped: true,
  isSortable: false,
  hoverRow: false,
  sidePadding: 10,
  size: K.SIZE.MEDIUM,
};

type TeamTableProps = {
  isWriteDisabled: boolean;
  userAccountData: UserAccountData | undefined;
  userAccountLoading: boolean;
  refetchData: () => void;
};

export const TeamTable: FC<TeamTableProps> = ({ isWriteDisabled, userAccountData, refetchData }: TeamTableProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const showSuccessAlert = (message: string): void => {
    setPageAlertConfig({ message, variant: PageAlertVariant.SUCCESS, autoHide: true });
  };

  const [resendInvite] = useMutation(RESEND_INVITE, {
    onCompleted: () => {
      showSuccessAlert('Invitation sent');
    },
  });
  const [revokeInvite] = useMutation(REVOKE_INVITE, {
    onCompleted: () => {
      showSuccessAlert('Invitation revoked');
    },
  });
  const [revokeAccess] = useMutation(REVOKE_ACCESS, {
    onCompleted: () => {
      showSuccessAlert('Access revoked');
    },
  });

  const byStatusThenEmail = (a: UserAccount, b: UserAccount): number => {
    if (a.status !== b.status) {
      return a.status === USER_STATUS.ACTIVE ? -1 : 1;
    }
    if (a.email !== b.email) {
      return numericStringCompare(a.email, b.email);
    }
    return 0;
  };

  const users = userAccountData?.userAccounts.slice().sort(byStatusThenEmail);
  const tableData =
    users?.map((row) =>
      makeRowData({
        ...row,
        isWriteDisabled,
        resendInvite,
        revokeInvite,
        revokeAccess,
        refetchData,
      }),
    ) || [];

  const makeDataTestId = (row: TableRow): string => createTestId(ELEMENT.userAccountsTeamTableRow, row?.emailValue);

  return <Table headers={TABLE_HEADERS} data={tableData} options={tableOptions} customRowIdCallback={makeDataTestId} />;
};
